import { Filter } from "features/History/Filter";
import { FilterData } from "features/History/Filter/FilterData";
import { TabIds } from "features/CenterContent/RoleContent/useTabsForRole";
import { HistoryDetail } from "features/History/HistoryDetail/HistoryDetail";
import { OperationalEvents } from "features/History/OperationalEvents";
import { HistoryWrapper } from "features/History/utils/HistoryCustomTableStyle";
import { FC, useEffect, useState } from "react";
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Box, VStack } from "shared/components/Spor";
import {
  CountryCode,
  countryCode as countryCodeEnum,
} from "shared/types/countryCode";
import { CombinedHistoryResponse } from "shared/types/operationalInformation";
import { isBeforeDate } from "shared/utils/dateUtils";
import * as ROUTES from "shared/utils/routes";
import { useDropsRole } from "stores/useDropsRole";

const setSessionStorageHistoryFilterData = (historyFilterData: FilterData) => {
  sessionStorage.setItem("historyFilterDataV2", historyFilterData.stringify());
};

const getSessionStorageHistoryFilterData = (
  countryCode: CountryCode,
): FilterData => {
  const filterDataFromSessionStorage: FilterData | null = FilterData.fromString(
    sessionStorage.getItem("historyFilterDataV2"),
  );
  const EIGHT_HOURS_MS = 8 * 60 * 60 * 1000;
  if (
    !filterDataFromSessionStorage ||
    // If it has been atleast 8 hours since you last updated the filter, then let it reset
    isBeforeDate(
      filterDataFromSessionStorage.updatedAt,
      new Date(),
      EIGHT_HOURS_MS,
    )
  ) {
    return new FilterData({ countryCode });
  }
  return filterDataFromSessionStorage;
};

export const HistoryOverview: FC = () => {
  const { isDriftledningen } = useDropsRole();
  const navigate = useNavigate();
  const location = useLocation();
  const { roleIdParam } = useParams<{ roleIdParam?: string }>();
  const [searchParams] = useSearchParams();
  const countryCode = isDriftledningen()
    ? countryCodeEnum.enum.SE
    : countryCodeEnum.enum.NO;

  const historyEventUuid = searchParams.get("historyEventUuid");
  const oversiktPath = roleIdParam
    ? generatePath(ROUTES.LANDING, {
        roleIdParam,
        tabIdParam: TabIds.HISTORY,
      })
    : undefined;

  const [filter, setFilter] = useState<FilterData>(
    getSessionStorageHistoryFilterData(countryCode),
  );

  useEffect(() => {
    setSessionStorageHistoryFilterData(filter);
  }, [filter]);

  const handleSelectEvent = (event: CombinedHistoryResponse) => {
    navigate({
      pathname: oversiktPath ?? location.pathname,
      search: new URLSearchParams({
        historyEventUuid: event.uuid,
      }).toString(),
    });
  };

  const handleBackToOverview = () => {
    navigate(oversiktPath ?? location.pathname, { replace: true });
  };

  if (historyEventUuid) {
    return (
      <HistoryWrapper>
        <VStack>
          <Box maxWidth="1700px" width="100%">
            <HistoryDetail
              eventUuid={historyEventUuid}
              setEventDetailUUID={(uuid) => {
                if (!uuid) {
                  handleBackToOverview();
                  return;
                }
                navigate(
                  {
                    pathname: oversiktPath ?? location.pathname,
                    search: new URLSearchParams({
                      historyEventUuid: uuid,
                    }).toString(),
                  },
                  { replace: true },
                );
              }}
            />
          </Box>
        </VStack>
      </HistoryWrapper>
    );
  }
  return (
    <HistoryWrapper>
      <VStack>
        <Box maxWidth="1700px" width="100%">
          <Filter filter={filter} setFilter={setFilter} />
          <OperationalEvents
            filter={filter}
            setFilter={setFilter}
            onSelectEvent={handleSelectEvent}
          />
        </Box>
      </VStack>
    </HistoryWrapper>
  );
};
